<template>
  <b-container class="details mb-2">
    <b-row class="mb-2 tabHeader">
      <b-col cols="12" class="font-weight-bold">{{ title }}</b-col>
    </b-row>
    <b-row>
      <b-col cols="12">
        <b-table striped hover :items="items" :fields="tableFields" class="results w-100"></b-table>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
// "Costi a scadenza" (CostsAtExpiration). Backend-wise this is a field-catalog section
// (EntryCost{Min,Max}/ExitCost{Min,Max}/OtherCosts{Min,Max}/TransactionCosts{Min,Max}/
// PerformanceFees{Min,Max} — DETAIL_PAGE_DESIGN.md's "Min/Max variant pair" catalog shape, same as
// MarketIndicators) — dispatched here via `fields`, not `payload` (same pattern as LiquiditySection.vue,
// see DetailSection.vue's own comment on that). It renders a real 2-column Min/Max table — one row
// per cost concept, not one row per field — hence this dedicated component instead of
// FieldCatalogSection.vue's generic `indicatorLayout`.
//
// MOP shows both Min/Max columns (a genuine range across investment options); SOP/SID/IC (single-Kpi
// aggregation) always has Min==Max for every concept, so it collapses to one column instead of two
// identical ones. No StructureType signal is available here (this section carries no
// payload, and reaching into the Sidebar via provide/inject for it would only ever agree with what the
// data itself already shows), so `isRange` is derived directly from the fields: MOP the moment any
// concept's Min differs from its Max.
//
// Column layout deliberately mirrors ProductPerformance.vue's own `.results`-based indicator table
// (same KpiKid tab, same "N indicators × Min/Max pair" shape) field-for-field — `[label, empty, min,
// max]` for the 2-column case, `[label, empty, empty2, min]` for the 1-column case — instead of a
// bespoke 2/3-column layout with custom CSS width overrides, which would misalign
// against every other table in the tab: every other `.results` table on this page always reserves
// exactly 4 column slots (1 label + 3 value, blank-padded when a slot is unused, e.g. a missing
// period) because the global `.results` width rule (40% label + 20% × 3) is tuned for exactly that
// count — matching the same 4-slot structure, blank-padded the same way, is what actually keeps every
// `.results` table's columns pixel-aligned, not fighting that shared rule with a per-component override.
import { detailSectionTitle } from '../../../utils/detailFieldFormat'

const CONCEPTS = ['entryCost', 'exitCost', 'otherCosts', 'transactionCosts', 'performanceFees']

export default {
  name: 'costs-at-expiration-section',
  props: {
    fields: { type: Array, required: true },
  },
  computed: {
    title() {
      return detailSectionTitle(this, 'CostsAtExpiration')
    },
    fieldMap() {
      const map = {}
      this.fields.forEach((field) => {
        map[field.key] = field.value
      })
      return map
    },
    rows() {
      return CONCEPTS.map((concept) => ({
        concept,
        min: this.fieldMap[`${concept}Min`],
        max: this.fieldMap[`${concept}Max`],
      }))
    },
    isRange() {
      return this.rows.some((row) => row.min != null && row.max != null && row.min !== row.max)
    },
    items() {
      return this.rows.map((row) => ({
        concept: this.$t(`productDetails.fields.CostsAtExpiration.${row.concept}`),
        min: this.formatPercent(row.min),
        max: this.formatPercent(row.max),
      }))
    },
    tableFields() {
      const ret = [
        { key: 'concept', label: this.$t('productDetails.fields.CostsAtExpiration.voiceColumn'), class: 'text-left' },
        { key: 'empty', label: '' },
      ]
      if (this.isRange) {
        ret.push({ key: 'min', label: 'Min', class: 'text-right' }, { key: 'max', label: 'Max', class: 'text-right' })
      } else {
        ret.push({ key: 'empty2', label: '' }, { key: 'min', label: '', class: 'text-right' })
      }
      return ret
    },
  },
  methods: {
    formatPercent(value) {
      return value != null && !isNaN(value) ? this.$n(value, 'percent') : '-'
    },
  },
}
</script>
